import { useMutation } from "@tanstack/react-query";
import { useCallback, useContext } from "react";
import { PromoCode, PromoCodeError, PromoParam } from "@lookiero/promocodes-front";
import { Country } from "@lookiero/sty-psp-locale";
import { OrderRepositoryImpl } from "../../domain/orderRepositoryImpl/OrderRepositoryImpl";
import { queryClientContext } from "../hooks/useQueryClient";
import { useStaticInfo } from "../hooks/useStaticInfo";
import { Device } from "../shared/types";
import { queryKey as getOrderSummaryForCheckout } from "./useGetOrderSummaryForCheckout";

interface UseApplyPromoAndRefetchOrderProps {
  readonly site: Country;
  readonly device: Device;
  readonly promo: {
    readonly code: string | null;
    readonly discountPercentage: number | null;
  };
  readonly promoCode: PromoParam;
  readonly onSuccess: (error: PromoCode) => void;
  readonly onError: (error: PromoCodeError) => void;
}

const orderRepo = OrderRepositoryImpl.getInstance();
const APPLY_PROMOCODE_I18N_KEY = "myLK.shipAndPay.promocode_toast_error";

const useApplyPromo = ({
  site,
  device,
  promo,
  promoCode,
  onSuccess: onSuccessApply,
  onError: onErrorApply,
}: UseApplyPromoAndRefetchOrderProps) => {
  const {
    session: { auth },
    apiUrl,
  } = useStaticInfo();

  const queryClient = useContext(queryClientContext);

  const mutation = useMutation(
    {
      mutationFn: async (promoCode: string) => {
        return orderRepo.applyPromo(auth, promoCode, device, site, apiUrl);
      },
    },
    queryClient,
  );

  const applyPromo = useCallback(
    (promoCodeResponse: PromoCode) => {
      const isAppliedAlready = promo && promoCodeResponse.name === promo.code;
      if (Boolean(isAppliedAlready) === false) {
        mutation.mutate(promoCodeResponse.name, {
          onSuccess: () => {
            queryClient?.invalidateQueries({
              queryKey: [getOrderSummaryForCheckout],
            });
            onSuccessApply(promoCodeResponse);
          },
          onError: () => {
            const PROMOCODE_APPLY_ERROR: PromoCodeError = {
              rawError: new Error(),
              promoCode,
              localise: { id: APPLY_PROMOCODE_I18N_KEY },
            };

            onErrorApply(PROMOCODE_APPLY_ERROR);
          },
        });
      }
    },
    [promo, promoCode, mutation, queryClient, onSuccessApply, onErrorApply],
  );

  return applyPromo;
};

export { useApplyPromo };
